import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Plus, Trash2, AlertTriangle, UserCog } from "lucide-react";
import { useAuth, useCabang } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Cabang() {
  const { user } = useAuth();
  const { cabangList, loadCabang } = useCabang();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [adminId, setAdminId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [transferOpen, setTransferOpen] = useState(null); // cabang object

  useEffect(() => {
    if (user && !["super_admin", "admin"].includes(user.role)) navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/users")
      .then(({ data }) => setAdmins(data.filter((u) => u.role === "admin" && (u.workspace_id === user?.active_workspace_id || u.active_workspace_id === user?.active_workspace_id))))
      .catch(() => setAdmins([]));
  }, [isSuper, user?.active_workspace_id]);

  const unassignedCount = useMemo(
    () => (isSuper ? cabangList.filter((c) => !c.admin_id).length : 0),
    [cabangList, isSuper]
  );

  const add = async (e) => {
    e.preventDefault();
    if (isSuper && !adminId) { toast.error("Pilih admin pemilik terlebih dahulu"); return; }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), code: code.trim() };
      if (isSuper) payload.admin_id = adminId;
      await api.post("/cabang", payload);
      toast.success("Cabang ditambahkan");
      setName(""); setCode(""); setAdminId("");
      loadCabang();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setSubmitting(false); }
  };

  const remove = async (id, cname) => {
    if (!isSuper) { toast.error("Hanya super admin yang bisa hapus cabang"); return; }
    if (!window.confirm(`Hapus cabang "${cname}"?`)) return;
    try {
      await api.delete(`/cabang/${id}`);
      toast.success("Cabang dihapus");
      loadCabang();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cabang"
        subtitle="Kelola daftar cabang usaha. Setiap cabang memiliki satu admin pemilik dan datanya terisolasi dari admin lain."
      />

      {isSuper && unassignedCount > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="unassigned-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Ada {unassignedCount} cabang tanpa pemilik admin.</div>
            <div>Klik <b>Ubah Pemilik</b> pada baris cabang untuk menetapkan admin. Selama belum ditetapkan, admin biasa tidak dapat mengakses cabang tersebut.</div>
          </div>
        </Card>
      )}

      <Card className="p-6 border-slate-200">
        <div className="font-heading text-base font-bold tracking-tight text-slate-900 mb-1">Tambah Cabang Baru</div>
        <div className="text-sm text-slate-500 mb-4">
          {isSuper
            ? "Pilih admin pemilik — hanya admin ini yang bisa akses cabang tersebut."
            : "Cabang yang Anda buat otomatis menjadi milik Anda."}
        </div>
        <form onSubmit={add} className={`grid grid-cols-1 gap-3 items-end ${isSuper ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          <div className="space-y-1.5">
            <Label htmlFor="c-name" className="text-sm text-slate-700">Nama Cabang</Label>
            <Input id="c-name" required value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Cabang Surabaya" data-testid="cabang-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-code" className="text-sm text-slate-700">Kode (opsional)</Label>
            <Input id="c-code" value={code} onChange={(e) => setCode(e.target.value)}
                   placeholder="SBY" data-testid="cabang-code" />
          </div>
          {isSuper && (
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Usaha Pemilik</Label>
              <Select value={adminId} onValueChange={setAdminId}>
                <SelectTrigger data-testid="cabang-admin"><SelectValue placeholder="Pilih usaha" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {admins.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">Belum ada usaha. Buat akun Usaha di halaman Pengguna.</div>
                  ) : admins.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="submit" disabled={submitting || (isSuper && !adminId)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-10" data-testid="cabang-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Tambah
          </Button>
        </form>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead>Nama</TableHead>
              <TableHead>Kode</TableHead>
              {isSuper && <TableHead>Pemilik (Usaha)</TableHead>}
              <TableHead>Dibuat oleh</TableHead>
              <TableHead className="w-[140px] text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="cabang-table">
            {cabangList.length === 0 ? (
              <TableRow><TableCell colSpan={isSuper ? 5 : 4} className="text-center py-12">
                <div className="flex flex-col items-center gap-2 text-slate-500">
                  <Building2 className="h-8 w-8 text-slate-300" />
                  <div className="font-medium">Belum ada cabang</div>
                </div></TableCell></TableRow>
            ) : cabangList.map((c) => (
              <TableRow key={c.id} data-testid={`cabang-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-slate-500 text-sm">{c.code || "—"}</TableCell>
                {isSuper && (
                  <TableCell className="text-sm">
                    {c.admin_id ? (
                      <span className="inline-flex flex-col">
                        <span className="text-slate-800 font-medium">{c.admin_name || "—"}</span>
                        <span className="text-xs text-slate-500">{c.admin_email || ""}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200">
                        <AlertTriangle className="h-3 w-3" /> Belum ditetapkan
                      </span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-slate-500 text-sm">{c.created_by}</TableCell>
                <TableCell className="text-right">
                  {isSuper && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setTransferOpen(c)}
                              data-testid={`cabang-transfer-${c.id}`} title="Ubah Pemilik">
                        <UserCog className="h-4 w-4 text-slate-700" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(c.id, c.name)}
                              data-testid={`cabang-delete-${c.id}`} title="Hapus">
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {transferOpen && (
        <TransferOwnerDialog
          cabang={transferOpen}
          admins={admins}
          onClose={() => setTransferOpen(null)}
          onSaved={() => { setTransferOpen(null); loadCabang(); }}
        />
      )}
    </div>
  );
}

function TransferOwnerDialog({ cabang, admins, onClose, onSaved }) {
  const [adminId, setAdminId] = useState(cabang.admin_id || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!adminId) { toast.error("Pilih admin"); return; }
    setSaving(true);
    try {
      await api.put(`/cabang/${cabang.id}/owner`, { admin_id: adminId });
      toast.success("Pemilik cabang diperbarui");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Ubah Pemilik — {cabang.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-slate-600">
            Cabang ini akan hanya dapat diakses oleh usaha yang dipilih. Data lama tetap utuh.
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Usaha Pemilik</Label>
            <Select value={adminId} onValueChange={setAdminId}>
              <SelectTrigger data-testid="transfer-admin"><SelectValue placeholder="Pilih usaha" /></SelectTrigger>
              <SelectContent className="bg-white">
                {admins.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">Belum ada usaha.</div>
                ) : admins.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="transfer-save">
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
